// 教師安排整頁（training-arrange-train＝legacy arrangeTrain.jsp：查詢表單＋匯入群＋14 欄表＋修改 modal）。
// 批次 15 Wave 3（B7-3，Gitea issue #13）。掛在 PageBehavior.complexBody（批次 13 hook 先例）。
//
// legacy 流程（行號逐檔親讀 2026-09-24，基準＝ePortfolio_work/.../views/training/arrangeTrain.jsp；
// 詳批次 15 執行文件 §1.3）：
//   - 查詢表單（:640-745）：*年度 annualId（label 是「年度」非「學年度」）/*醫院/*訓練類別
//     (job.type)/*訓練計畫(plan.target)／訓練階段執行部門 phaseDepartmentId／子階段名稱
//     phaseTargetId／訓練階段起訖 phaseStartDate+phaseEndDate（readonly）＋reset／學員
//     searchStudentCode+studentId／臨床教師 searchClinicalCode+clinicalId／階段導師
//     searchMentorCode+mentorId／查詢（tarainArrange-ACCESS 權限字串 typo 照實）＋isDeptMan hidden。
//   - 右欄（:755-790，tarainArrange-UPDATE）：匯入階段資料：label＋uploadFile(.xlsx)＋匯入→
//     importExcel()／使用說明:＋範本下載（#donwloadId typo 照實）→downloadExcel()＋ol li×4 逐字。
//   - 表欄（:115-188）14 欄：訓練期間(起迄) `{start}<br>{end}`／科部 phaseDepartmentName／
//     訓練階段 getPhaseName（parent1/2 Group/Phase 以 `/` 串）／訓練子階段 phaseTargetName
//     (+`(phaseName2)`)／學員姓名 a.showEdit→showPhaseInst(row)／學員員編／行動電話(公務)
//     officeMobile／簡碼 officeCode／臨床教師 clinicalIdName(+BR×2)／階段導師 mentor1Name(+BR)／
//     訓練地點 locationName＋isDeptMan→完成份數（表頭 a onclick openFormList()；cell
//     formDone/formTodo）＋完成度（showPercentage）＋狀態（1→啟用中 else 停用中）。
//   - doQuery（:282-320）：起訖都有且 !isAfterOrSame→`日期選取不正確!`→getTrainArrangeListAjax→
//     clinicalList/mentorList/wardList（雛形＝種子池）。
//   - showPhaseInst＝修改 modal（:341-490）：姓名 studentIdName／訓練階段 parent1Name／訓練子階段
//     phaseTargetIdName(+phaseName2)／臨床教師1-3（clinicalIdNDept+clinicalIdN）／階段導師1-2／
//     訓練地點 wardId／暫停訓練 stopTrain（isActive!=1→checked）＋mentorAmount（0→藏 1,2；1→藏 2）
//     ＋clinicalAmount（1→藏 2,3；2→藏 3）＋footer 關閉/存檔。
//   - save（:494-506）：isActive=stopTrain checked?0:1→saveTrainArrangeAjax→hide＋info＋doQuery。
//   - downloadExcel（:515-532）：$.fileDownload downloadTrainArrangeExcel GET；失敗 NoData→
//     `查不到資料!` else `下載檔案失敗！`（雛形＝Blob 下載，批次 9 先例）。
//   - importExcel（:534-555）：空→`未選擇檔案！`→FormData excelFile→importTrainArrangeExcel→
//     成功清檔＋info＋doQuery（雛形＝成功訊息 0 data-effect，批次 14 先例）。
//   - openFormList（:592-630）→`/form/planFormList?phaseInstId=..&phaseDepartmentId=..&phaseTargetId=..
//     &planTargetId=..`：跨頁參數相依→雛形提示呈現（B4-1 先例），登記 KNOWN-NOT-DONE。
//
// 雛形簡化（【雛形值待實機校準】）：級聯下拉→種子平列；日期→文字輸入；isDeptMan/isMentor＝
// admin 視角 true（D9）；修改 modal 的科部連動（setDepartmentElementNoLimit）→渲染不過濾；
// 匯入/範本＝Blob/成功訊息。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Checkbox } from '@/components/base/Checkbox';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ModalFrame, useDialogs } from '@/components/layouts';
import { LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { t } from '@/messages/zh-TW';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';
import { FormWrap } from '@/pages/templates/shared';
import {
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_ANNUAL_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  TRAINING_CLINICAL_OPTIONS,
  TRAINING_JOB_TYPE_OPTIONS,
  TRAINING_MENTOR_DEPT_OPTIONS,
  TRAINING_MENTOR_OPTIONS,
  TRAINING_PHASE_TARGET_OPTIONS,
  TRAINING_STUDENT_OPTIONS,
  TRAINING_WARD_OPTIONS,
} from '@/mocks/resource-rules';
import { isAfterOrSame } from '@/pages/report/ReportKit';

/** showPercentage（arrangeTrain.jsp:578-586 同 planInstList_js.jsp:532-536）：100 藍／0 黑／紅。 */
function percentageOf(p: string): ReactElement {
  if (p === '100') {
    return <span className="text-blue-600">100%</span>;
  }
  if (p === '0') {
    return <span className="text-black">0%</span>;
  }
  return <span className="text-red-600">{p}%</span>;
}

/** getPhaseName（:270-287）：parent1/2 的 Group/Phase 名稱以 `/` 串接（Group 先）。 */
function phaseNameOf(row: Row): string {
  const parts: string[] = [];
  for (const key of ['parent1', 'parent2']) {
    const type = String(row[`${key}PhaseType`] ?? '');
    if (type === 'Group' || type === 'Phase') {
      parts.push(String(row[`${key}Name`] ?? ''));
    }
  }
  return parts.filter((part) => part !== '').join('/');
}

export function ArrangeTrainBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 查詢表單（:640-745）。
  // ------------------------------------------------------------------
  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState('org-1');
  const [jobTypeId, setJobTypeId] = useState('3');
  const [planTargetId, setPlanTargetId] = useState('plan-1');
  const [phaseDepartmentId, setPhaseDepartmentId] = useState('');
  const [phaseTargetId, setPhaseTargetId] = useState('');
  const [phaseStartDate, setPhaseStartDate] = useState('');
  const [phaseEndDate, setPhaseEndDate] = useState('');
  const [searchStudentCode, setSearchStudentCode] = useState('');
  const [studentId, setStudentId] = useState('');
  const [searchClinicalCode, setSearchClinicalCode] = useState('');
  const [clinicalId, setClinicalId] = useState('');
  const [searchMentorCode, setSearchMentorCode] = useState('');
  const [mentorId, setMentorId] = useState('');

  // ------------------------------------------------------------------
  // 清單（doQuery :282-320）。
  // ------------------------------------------------------------------
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);
  const appliedRef = useRef<Record<string, string>>({});
  const load = (filters: Record<string, string>) => {
    void (async () => {
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 50, filters });
        setRows(result.rows);
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };
  const doQuery = () => {
    // 日期選取不正確!（:292-293：起訖都有且 !isAfterOrSame；common.js:650-655 鏡像）。
    if (!isAfterOrSame(phaseStartDate, phaseEndDate)) {
      void dialogs.alert('日期選取不正確!');
      return;
    }
    const filters: Record<string, string> = {
      annualId,
      organizationId,
      jobTypeId,
      planTargetId,
      phaseDepartmentId,
      phaseTargetId,
      searchStudentCode,
      studentId,
      searchClinicalCode,
      clinicalId,
      searchMentorCode,
      mentorId,
      phaseStartDate,
      phaseEndDate,
      // 訓練階段起訖進 filters（C5-h：帶 name 控制項的值要進 DataSource；matchesFilters
      // 精確比對、無區間語意→驅動值下多為 0 列，legacy 為伺服器端區間過濾【雛形值待實機校準】）。
    };
    appliedRef.current = filters;
    load(filters);
  };

  // ------------------------------------------------------------------
  // 修改 modal（showPhaseInst :341-490／save :494-506）。
  // ------------------------------------------------------------------
  const [editing, setEditing] = useState<Row | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [stopTrain, setStopTrain] = useState(false);
  const openEdit = (row: Row) => {
    setEditing(row);
    setEditValues({
      clinicalId: String(row['clinicalId'] ?? ''),
      clinicalId2: String(row['clinicalId2'] ?? ''),
      clinicalId3: String(row['clinicalId3'] ?? ''),
      mentorId1: String(row['mentorId1'] ?? ''),
      mentorId2: String(row['mentorId2'] ?? ''),
      wardId: String(row['wardId'] ?? ''),
    });
    setStopTrain(String(row['isActive'] ?? '') !== '1'); // isActive!=1→checked（:398-402）
  };
  const saveEdit = () => {
    void (async () => {
      if (editing === null) {
        return;
      }
      try {
        // save（:494-506）：isActive=stopTrain checked?0:1→update op（列上同步 mentor/clinical/ward）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(editing.id ?? ''), {
            ...editValues,
            isActive: stopTrain ? '0' : '1',
          }),
        );
        setEditing(null);
        await dialogs.info(t('state.saveSuccess'));
        doQuery();
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof Error ? cause.message : t('error.generic'));
      }
    })();
  };

  // ------------------------------------------------------------------
  // 匯入/範本（:534-555/:515-532）。
  // ------------------------------------------------------------------
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const runImport = () => {
    void (async () => {
      if (uploadFile === null) {
        await dialogs.alert('未選擇檔案！'); // :548
        return;
      }
      await dialogs.info('匯入成功（雛形不處理檔案內容）【雛形值待實機校準】');
      setUploadFile(null);
      doQuery();
    })();
  };
  const downloadTemplate = () => {
    // downloadExcel（:515-532）：$.fileDownload→Blob 下載（批次 9 先例【雛形值待實機校準】）；
    // 失敗訊息（查不到資料!/下載檔案失敗！）在 Blob 模式不會發生，不實作。
    if (typeof URL.createObjectURL !== 'function') {
      return;
    }
    const blob = new Blob(
      ['React 雛形佔位範本（legacy 來源：/training/arrange/downloadTrainArrangeExcel，實機校準後由真後端提供）'],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '教師安排範本.xlsx';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /** openFormList（:588-638）→`/form/planFormList?...`：跨頁參數相依→提示呈現（B4-1 先例）。 */
  const openFormList = () => {
    void dialogs.alert(
      `開啟計畫表單查詢：/form/planFormList?phaseInstId=&phaseDepartmentId=${phaseDepartmentId || '-1'}&phaseTargetId=${phaseTargetId || '-1'}&planTargetId=${planTargetId || '-1'}【雛形值待實機校準】`,
    );
  };

  // 首載一次（legacy 需手動查詢；雛形 ready 即查詢＝批次 14 先例）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 首載一次。
  useEffect(() => {
    doQuery();
  }, []);

  // isDeptMan（雛形 admin 視角＝true，D9）→完成份數/完成度欄顯示（:169-176）。
  const isDeptMan = true;

  const columns: readonly TableColumn[] = [
    { key: 'period', header: '訓練期間(起迄)', align: 'center' },
    { key: 'phaseDepartmentName', header: '科部', align: 'center' },
    { key: 'parentPhase', header: '訓練階段', align: 'center' },
    { key: 'subPhase', header: '訓練子階段', align: 'center' },
    { key: 'studentName', header: '學員姓名', align: 'center' },
    { key: 'studentEmployeeId', header: '學員員編', align: 'center' },
    { key: 'officeMobile', header: '行動電話(公務)', align: 'center' },
    { key: 'officeCode', header: '簡碼', align: 'center' },
    { key: 'clinicalNames', header: '臨床教師', align: 'center' },
    { key: 'mentorNames', header: '階段導師', align: 'center' },
    { key: 'locationName', header: '訓練地點', align: 'center' },
    ...(isDeptMan
      ? ([
          {
            key: 'formDone',
            // 完成份數欄頭＝openFormList 連結（arrangeTrain.jsp:169-171 表頭 a onclick）。
            header: (
              <a
                data-ui-id={`${pageId}-result-head-form-done-link`}
                className={`${LINK_TEXT_CLASS} cursor-pointer`}
                onClick={openFormList}
              >
                完成份數
              </a>
            ),
            align: 'center' as const,
          },
          { key: 'formDonePercentage', header: '完成度', align: 'center' as const },
        ] as const)
      : []),
    { key: 'statusText', header: '狀態', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="教師安排"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        <div className="grid gap-4 md:grid-cols-12">
          {/* 批次 37（GNL-005-b Phase 2）：查詢欄位＋查詢鈕包 form 鏡像 legacy（spec /training/arrange POST）。 */}
          <FormWrap pageId={pageId}>
          <div className="md:col-span-7 space-y-3" data-ui-id={`${pageId}-query-form`}>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>年度：
              </label>
              <div className="w-48">
                <Select uiId={`${pageId}-query-annual-id`} name="annualId" value={annualId} onChange={(next) => setAnnualId(next)} options={selectOptionsOverride(pageId, 'annualId') ?? COURSE_CLAZZ_ANNUAL_OPTIONS} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>醫院：
              </label>
              <div className="w-48">
                <Select uiId={`${pageId}-query-organization-id`} name="organizationId" value={organizationId} onChange={(next) => setOrganizationId(next)} options={selectOptionsOverride(pageId, 'organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>訓練類別：
              </label>
              <div className="w-48">
                <Select
                  uiId={`${pageId}-query-job-type-id`}
                  name="jobTypeId"
                  value={jobTypeId}
                  onChange={(next) => setJobTypeId(next)}
                  options={selectOptionsOverride(pageId, 'jobTypeId') ?? TRAINING_JOB_TYPE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                  {...(selectOptionsOverride(pageId, 'jobTypeId') ? {} : { placeholder: '請選擇' })}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">
                <span className="text-red-600">*</span>訓練計畫：
              </label>
              <div className="w-64">
                <Select uiId={`${pageId}-query-plan-target-id`} name="planTargetId" value={planTargetId} onChange={(next) => setPlanTargetId(next)} options={selectOptionsOverride(pageId, 'planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">訓練階段執行部門：</label>
              <div className="w-48">
                <Select uiId={`${pageId}-query-phase-department-id`} name="phaseDepartmentId" value={phaseDepartmentId} onChange={(next) => setPhaseDepartmentId(next)} options={selectOptionsOverride(pageId, 'phaseDepartmentId') ?? [{ value: '', label: '' }, ...TRAINING_MENTOR_DEPT_OPTIONS]} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">子階段名稱：</label>
              <div className="w-48">
                <Select uiId={`${pageId}-query-phase-target-id`} name="phaseTargetId" value={phaseTargetId} onChange={(next) => setPhaseTargetId(next)} options={selectOptionsOverride(pageId, 'phaseTargetId') ?? [{ value: '', label: '' }, ...TRAINING_PHASE_TARGET_OPTIONS]} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">訓練階段起訖：</label>
              {/* 原生 type=date（批次 14 先例：C5-h drive 相容；legacy readonly datepicker →
                ISO 顯示【雛形值待實機校準】）。 */}
              <input
                data-ui-id={`${pageId}-query-phase-start-date`}
                name="phaseStartDate"
                type="date"
                className="rounded border border-border px-2 py-1 text-sm"
                value={phaseStartDate}
                onChange={(event) => setPhaseStartDate(event.target.value)}
              />
              <input
                data-ui-id={`${pageId}-query-phase-end-date`}
                name="phaseEndDate"
                type="date"
                className="rounded border border-border px-2 py-1 text-sm"
                value={phaseEndDate}
                onChange={(event) => setPhaseEndDate(event.target.value)}
              />
              <Button
                uiId={`${pageId}-query-dates-reset`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setPhaseStartDate('');
                  setPhaseEndDate('');
                }}
              >
                清除
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">學員：</label>
              <div className="w-40">
                <TextInput uiId={`${pageId}-query-search-student-code`} name="searchStudentCode" value={searchStudentCode} placeholder="(請輸入學員姓名或學員編號)" onChange={(next) => setSearchStudentCode(next)} />
              </div>
              <div className="w-48">
                <Select uiId={`${pageId}-query-student-id`} name="studentId" value={studentId} onChange={(next) => setStudentId(next)} options={selectOptionsOverride(pageId, 'studentId') ?? [{ value: '', label: '' }, ...TRAINING_STUDENT_OPTIONS]} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">臨床教師：</label>
              <div className="w-40">
                <TextInput uiId={`${pageId}-query-search-clinical-code`} name="searchClinicalCode" value={searchClinicalCode} placeholder="(請輸入姓名或員編)" onChange={(next) => setSearchClinicalCode(next)} />
              </div>
              <div className="w-48">
                <Select uiId={`${pageId}-query-clinical-id`} name="clinicalId" value={clinicalId} onChange={(next) => setClinicalId(next)} options={selectOptionsOverride(pageId, 'clinicalId') ?? [{ value: '', label: '' }, ...TRAINING_CLINICAL_OPTIONS]} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="w-28 shrink-0 text-right text-sm">階段導師：</label>
              <div className="w-40">
                <TextInput uiId={`${pageId}-query-search-mentor-code`} name="searchMentorCode" value={searchMentorCode} placeholder="(請輸入姓名或員編)" onChange={(next) => setSearchMentorCode(next)} />
              </div>
              <div className="w-48">
                <Select uiId={`${pageId}-query-mentor-id`} name="mentorId" value={mentorId} onChange={(next) => setMentorId(next)} options={selectOptionsOverride(pageId, 'mentorId') ?? [{ value: '', label: '' }, ...TRAINING_MENTOR_OPTIONS]} />
              </div>
            </div>
            <div>
              {/* 查詢（complexBodySubmitId 自報＝C5-h 送出；tarainArrange-ACCESS typo 照實註解）。 */}
              <Button uiId={`${pageId}-query-btn`} variant="default" size="sm" type="button" disabled={busy} onClick={doQuery}>
                查詢
              </Button>
            </div>
          </div>
          </FormWrap>
          <div className="md:col-span-5 space-y-2" data-ui-id={`${pageId}-import-area`}>
            <p className="text-sm font-bold">匯入階段資料：</p>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".xlsx"
                data-ui-id={`${pageId}-import-file`}
                // legacy name=uploadFile（serialize）；雛形匯入不接 DataSource（0 data-effect）→
                // 不帶 name（C5-h 分母只計帶 name 控制項；批次 14 reportControls 先例）。
                className="text-xs"
                onChange={(event) => {
                  setUploadFile(event.target.files?.[0] ?? null);
                }}
              />
              <Button uiId={`${pageId}-import-submit`} variant="primary" size="sm" type="button" onClick={runImport}>
                匯入
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">使用說明:</span>
              <Button uiId={`${pageId}-import-template`} variant="default" size="sm" type="button" onClick={downloadTemplate}>
                範本下載
              </Button>
            </div>
            <ol className="ml-5 list-decimal text-sm">
              <li>請先查詢出需安排師資的學員資料，再下載範本</li>
              <li>請依據範本檔案欄位說明，維護師資資料及訓練地點</li>
              <li className="font-bold text-red-600">
                西醫UGY安排臨床教師時
                <ul className="ml-4 list-disc">
                  <li>第1順位請指定住院醫師</li>
                  <li>第2順位請指定主治醫生</li>
                </ul>
              </li>
              <li>注意：透過匯入安排學員子階段教師，若匯入的檔案教師欄位為空值，會將現有已安排的學員教師覆蓋並清除</li>
            </ol>
          </div>
        </div>

        <div className="mt-4">
          <Table
            uiId={`${pageId}-result-table`}
            columns={columns}
            rows={rows}
            rowKey={(row) => toUiKey(String(row.id ?? ''))}
            emptyText="查無符合的資料"
            renderCell={(row, columnKey) => {
              const uiKey = toUiKey(String(row.id ?? ''));
              if (columnKey === 'period') {
                return (
                  <span>
                    {String(row['phaseStartDate'] ?? '')}
                    <br />
                    {String(row['phaseEndDate'] ?? '')}
                  </span>
                );
              }
              if (columnKey === 'phaseDepartmentName') {
                return String(row['phaseDepartmentName'] ?? '');
              }
              if (columnKey === 'parentPhase') {
                return phaseNameOf(row);
              }
              if (columnKey === 'subPhase') {
                const phaseName2 = String(row['phaseName2'] ?? '');
                const name = String(row['phaseTargetName'] ?? '');
                return phaseName2 === '' ? name : `${name}(${phaseName2})`;
              }
              if (columnKey === 'studentName') {
                return (
                  <a
                    data-ui-id={`${pageId}-row-${uiKey}-student-link`}
                    className={`${LINK_TEXT_CLASS} cursor-pointer`}
                    onClick={() => openEdit(row)}
                  >
                    {String(row['studentName'] ?? '')}
                  </a>
                );
              }
              if (columnKey === 'studentEmployeeId') {
                return String(row['studentEmployeeId'] ?? '');
              }
              if (columnKey === 'officeMobile') {
                return String(row['officeMobile'] ?? '');
              }
              if (columnKey === 'officeCode') {
                return String(row['officeCode'] ?? '');
              }
              if (columnKey === 'clinicalNames') {
                return [row['clinicalIdName'], row['clinicalId2Name'], row['clinicalId3Name']]
                  .filter((part) => String(part ?? '') !== '')
                  .join(' / ');
              }
              if (columnKey === 'mentorNames') {
                return [row['mentor1Name'], row['mentor2Name']]
                  .filter((part) => String(part ?? '') !== '')
                  .join(' / ');
              }
              if (columnKey === 'locationName') {
                return String(row['locationName'] ?? '');
              }
              if (columnKey === 'formDone') {
                if (row['formTodo'] === undefined) {
                  return '';
                }
                return `${String(row['formDone'] ?? '')}/${String(row['formTodo'] ?? '')}`;
              }
              if (columnKey === 'formDonePercentage') {
                if (row['formDonePercentage'] === undefined) {
                  return '';
                }
                return percentageOf(String(row['formDonePercentage'] ?? ''));
              }
              if (columnKey === 'statusText') {
                return String(row['isActive'] ?? '') === '1' ? '啟用中' : '停用中';
              }
              return '';
            }}
          />
        </div>
      </ContentPanel>

      {/* 修改 modal（dialogModal；showPhaseInst :341-490）。 */}
      <ModalFrame
        uiId={`${pageId}-edit-dialog`}
        open={editing !== null}
        title="修改"
        size="lg"
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button uiId={`${pageId}-edit-cancel`} variant="default" size="sm" type="button" onClick={() => setEditing(null)}>
              關閉
            </Button>
            <Button
              uiId={`${pageId}-edit-save`}
              variant="primary"
              size="sm"
              type="button"
              disabled={busy}
              dataEffect={`update:${pageId}`}
              onClick={saveEdit}
            >
              存檔
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">姓名：</label>
            <span data-ui-id={`${pageId}-edit-student-name`} className="text-sm font-bold">
              {String(editing?.['studentName'] ?? '')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">訓練階段：</label>
            <span className="text-sm">{phaseNameOf(editing ?? {})}</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">訓練子階段：</label>
            <span className="text-sm">
              {(() => {
                if (editing === null) {
                  return '';
                }
                const name = String(editing['phaseTargetName'] ?? '');
                const phaseName2 = String(editing['phaseName2'] ?? '');
                return phaseName2 === '' ? name : `${name}(${phaseName2})`;
              })()}
            </span>
          </div>
          {/* 臨床教師1-3／階段導師1-2（clinicalAmount/mentorAmount 決定顯隱；雛形以種子值呈現）。 */}
          {[1, 2, 3].map((index) => {
            const key = index === 1 ? 'clinicalId' : `clinicalId${index}`;
            const divKey = `clinicalId${index}Div`;
            const amount = Number(String(editing?.['clinicalAmount'] ?? '0'));
            if (amount < index) {
              return null;
            }
            return (
              <div key={divKey} className="flex items-center gap-2" data-ui-id={`${pageId}-edit-${divKey}`}>
                <label className="w-28 shrink-0 text-right text-sm">
                  臨床教師{index}：
                </label>
                <div className="w-40">
                  <Select
                    uiId={`${pageId}-edit-clinical-dept-${index}`}
                    value=""
                    onChange={() => undefined}
                    options={[{ value: '', label: '' }, ...TRAINING_MENTOR_DEPT_OPTIONS]}
                  />
                </div>
                <div className="w-48">
                  <Select
                    uiId={`${pageId}-edit-clinical-id-${index}`}
                    value={editValues[key] ?? ''}
                    onChange={(next) => setEditValues((current) => ({ ...current, [key]: next }))}
                    options={[{ value: '', label: '請選擇' }, ...TRAINING_CLINICAL_OPTIONS]}
                  />
                </div>
              </div>
            );
          })}
          {[1, 2].map((index) => {
            const key = `mentorId${index}`;
            const divKey = `mentorId${index}Div`;
            const amount = Number(String(editing?.['mentorAmount'] ?? '0'));
            if (amount < index) {
              return null;
            }
            return (
              <div key={divKey} className="flex items-center gap-2" data-ui-id={`${pageId}-edit-${divKey}`}>
                <label className="w-28 shrink-0 text-right text-sm">階段導師{index}：</label>
                <div className="w-40">
                  <Select
                    uiId={`${pageId}-edit-mentor-dept-${index}`}
                    value=""
                    onChange={() => undefined}
                    options={[{ value: '', label: '' }, ...TRAINING_MENTOR_DEPT_OPTIONS]}
                  />
                </div>
                <div className="w-48">
                  <Select
                    uiId={`${pageId}-edit-mentor-id-${index}`}
                    value={editValues[key] ?? ''}
                    onChange={(next) => setEditValues((current) => ({ ...current, [key]: next }))}
                    options={[{ value: '', label: '請選擇' }, ...TRAINING_MENTOR_OPTIONS]}
                  />
                </div>
              </div>
            );
          })}
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">訓練地點：</label>
            <div className="w-48">
              <Select
                uiId={`${pageId}-edit-ward-id`}
                value={editValues['wardId'] ?? ''}
                onChange={(next) => setEditValues((current) => ({ ...current, wardId: next }))}
                options={[{ value: '', label: '請選擇' }, ...TRAINING_WARD_OPTIONS]}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="w-28 shrink-0 text-right text-sm">暫停訓練：</label>
            <Checkbox
              uiId={`${pageId}-edit-stop-train`}
              checked={stopTrain}
              onChange={(next) => setStopTrain(next)}
            />
          </div>
        </div>
      </ModalFrame>
    </div>
  );
}
